import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, fullName, phoneNumber = null, notificationPreference = 'email') => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      email,
      password,
      full_name: fullName,
      phone_number: phoneNumber,
      notification_preference: notificationPreference
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (updates) => {
    try {
      await axios.put(`${API_URL}/api/auth/profile`, updates);
      setUser(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  const updateLocation = async (latitude, longitude) => {
    try {
      await axios.put(`${API_URL}/api/auth/location`, { latitude, longitude });
      setUser(prev => ({ ...prev, location: { latitude, longitude } }));
    } catch (error) {
      console.error('Failed to update location:', error);
    }
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  // Tier helper functions
  const hasTier = (tier) => {
    return user?.active_tiers?.includes(tier) || false;
  };

  const hasAnyTier = (tiers) => {
    return tiers.some(tier => user?.active_tiers?.includes(tier));
  };

  const hasInnerCircleAccess = () => {
    return hasAnyTier(['inner_circle', 'inner_edge', 'coaching_activation', 'sdc']);
  };

  const hasCoachAccess = () => {
    return hasAnyTier(['coaching_activation', 'sdc']);
  };

  const hasInnerEdgeAccess = () => {
    return hasAnyTier(['inner_edge', 'coaching_activation', 'sdc']);
  };

  const hasSDCAccess = () => {
    return hasTier('sdc');
  };

  const hasCostaRicaAccess = () => {
    return hasTier('costa_rica');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      updateProfile,
      updateLocation,
      refreshUser,
      // Tier helpers
      hasTier,
      hasAnyTier,
      hasInnerCircleAccess,
      hasCoachAccess,
      hasInnerEdgeAccess,
      hasSDCAccess,
      hasCostaRicaAccess
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
